import { type HTMLAttributes, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { DeprecatedLinkButton as LinkButton, Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import customCss from '@/app/components/hotel-detail/css/customCss';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import ModalHeader from '@/app/components/search/components/Modal/LayoutElements/ModalHeader';
import type { Brand } from '@/domainLogic/index.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useIsClamped } from '@/app/common/hooks/useIsClamped';
import { toTADisplayScore } from '@/app/components/hotel-detail/trip-advisor/tripadvisor.helper';

/** Out of what a card rates the review: the provider's own 10, or TripAdvisor's 5 beside a TA score. */
export type GuestReviewRatingScale = 5 | 10;

type HotelReviewWithLogo = HotelReview & { logoUrl: string; ratingScale?: GuestReviewRatingScale };

// The provider rates 1–10. Beside a TripAdvisor score the card moves to TA's 1–5 through the very
// conversion the TA scores get (Supply's 10–100 → 1–5, one decimal): 8/10 reads 4.1/5 (MPH-602).
export function formatGuestReviewRating(rating: string, scale: GuestReviewRatingScale = 10): string {
  const value = parseFloat(rating);
  return scale === 5 && Number.isFinite(value) ? toTADisplayScore(value * 10) : rating;
}

export const ReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  flex: 1 0 0;
  align-self: stretch;

  background-color: ${(p) => p.theme.commons.colors.gray0};
`;

const ReviewContainerHorizontal = styled.div`
  display: flex;
  flex-direction: row;
  row-gap: 16px;
`;

const ReviewHeader = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 8px;
  width: 100%;
`;

const ReviewHeaderHorizontal = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  min-width: 160px;
  width: 160px;
`;

const Avatar = styled.div`
  display: flex;
  width: 32px;
  height: 32px;
  padding: 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  border-radius: 16px;
  color: ${(p) => p.theme.kallisto.color.text.static.textPrimary};
  background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundDisabled};
`;

const Rating = styled.div`
  display: flex;
  align-items: baseline;
  flex-grow: 1;
  justify-content: flex-end;
`;

function ReviewRating({ rating, scale = 10 }: { rating: string; scale?: GuestReviewRatingScale }) {
  return (
    <Rating>
      <Text variant="Body" weight="bold">
        {formatGuestReviewRating(rating, scale)}
      </Text>
      <Text variant="BodySmall">/{scale}</Text>
    </Rating>
  );
}

const ReviewText4Lines = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  margin-top: 16px;
  flex-grow: 1;

  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
`;

const ReviewTextHorizontal = styled.div`
  flex-grow: 1;
  font-size: ${(p) => p.theme.commons.font.sizes.base};
  text-wrap: pretty;
`;

const ReviewText = styled.div`
  font-size: ${(p) => p.theme.commons.font.sizes.base};
  text-wrap: pretty;
`;

const SourceLogo = styled.img`
  margin-top: 16px;
  height: 16px;
`;

const ReviewerName = styled(Text)<{ $forceSmall?: boolean }>`
  ${(p) => p.theme.kallisto.mixins.font('BodySmall')};
  font-weight: ${(p) => p.theme.kallisto.font.weight.bold};
  ${(p) =>
    !p.$forceSmall &&
    `@media ${customCss.breakpoints.sm} {
      ${p.theme.kallisto.mixins.font('Body')}
      font-weight: ${p.theme.kallisto.font.weight.bold};
    }`}
`;

const ReviewerDate = styled(Text)<{ $forceSmall?: boolean }>`
  ${(p) => p.theme.kallisto.mixins.font('Caption')};
  color: ${(p) => p.theme.kallisto.color.text.static.textSecondary};
  ${(p) =>
    !p.$forceSmall &&
    `@media ${customCss.breakpoints.sm} {
      ${p.theme.kallisto.mixins.font('BodySmall')}
    }`}
`;

export function ReviewCardWithReadMore(props: HotelReviewWithLogo) {
  const { name, submitDate, rating, text, source, logoUrl, ratingScale } = props;
  const { t } = useTranslation();
  const { staticData, commonData } = usePageWrapperData();
  const { isEvoLayout } = useCommonClient();
  const isMobile = commonData.isMobileDevice;
  const textRef = useRef<HTMLDivElement>(null);
  const [isReviewModalOpen, setReviewModalOpen] = useState<boolean | undefined>();
  // The modal only adds value while the clamp is hiding something, and what it hides
  // changes after mount: the card stretches with its carousel row and the web font swaps in.
  const showReadMore = useIsClamped(textRef);

  return (
    <>
      <ReviewCardVertical
        {...{ name, submitDate, rating, source, logoUrl, ratingScale }}
        css={css`
          padding: 16px;
          border-radius: 8px;
          border: 1px solid ${(p) => p.theme.commons.colors.gray100};
          min-width: 240px;
          @media ${customCss.breakpoints.sm} {
            min-width: auto;
          }
        `}
      >
        <ReviewText4Lines ref={textRef} css={showReadMore ? `margin-bottom: 8px;` : undefined}>
          {text}
        </ReviewText4Lines>
        {showReadMore ? (
          <LinkButton
            variant="secondary"
            appearance="underlined"
            role="link"
            onClick={() => setReviewModalOpen(true)}
            aria-label={`${t('vacation.select.details.guest.reviews.selected.show')} - ${name}`}
            css={css`
              ${(p) => p.theme.kallisto.mixins.font('BodySmall')};
            `}
          >
            {t('vacation.select.details.guest.reviews.selected.show')}
          </LinkButton>
        ) : null}
      </ReviewCardVertical>

      <OpenXModal
        isOpen={isReviewModalOpen ?? false}
        isMobile={isMobile ?? true}
        brand={staticData.domainValues?.brand as Brand}
        onClose={() => setReviewModalOpen(false)}
        onCloseByBrowserHistory={() => setReviewModalOpen(false)}
        onOpenByBrowserHistory={() => setReviewModalOpen(true)}
        fullScreen={false}
        id="guest-review-modal"
        aria-label={t('vacation.select.details.guest.reviews.title')}
        bodyBackgroundColor="backgroundPage"
        // ModalHeader switches to the Evo header (close button on the right) on Evo pages, whose
        // scroll-driven shadow needs scrollShadows.
        scrollShadows={isEvoLayout}
        header={{
          sticky: true,
          element: (
            <ModalHeader handleClose={() => setReviewModalOpen(false)}>
              {t('vacation.select.details.guest.reviews.title')}
            </ModalHeader>
          ),
        }}
      >
        {isMobile ? (
          <ReviewCardVerticalFullText {...props} />
        ) : (
          <ReviewCardHorizontal
            {...props}
            css={`
              padding: 24px;
            `}
          />
        )}
      </OpenXModal>
    </>
  );
}

export function ReviewCardVerticalFullText(props: HotelReviewWithLogo) {
  const { text, ...rest } = props;
  return (
    <ReviewCardVertical {...rest}>
      <ReviewText
        css={`
          margin-top: 16px;
        `}
      >
        {text}
      </ReviewText>
    </ReviewCardVertical>
  );
}

type ReviewCardVerticalProps = Omit<HotelReviewWithLogo, 'text'> & {
  children: React.ReactNode;
} & React.HTMLAttributes<HTMLElement>;

function ReviewCardVertical(props: ReviewCardVerticalProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { name, submitDate, rating, source, tripReason, travelCompanion, logoUrl, ratingScale, children, ...rest } =
    props;

  return (
    <ReviewContainer {...rest}>
      <ReviewHeader>
        <Avatar css={'align-self: center;'}>{name.charAt(0).toUpperCase()}</Avatar>
        <div
          css={`
            display: flex;
            flex-direction: column;
          `}
        >
          <ReviewerName>{name}</ReviewerName>
          <ReviewerDate>
            {tripReason && travelCompanion
              ? `${tripReason} (${travelCompanion}) · `
              : tripReason || travelCompanion
                ? `${tripReason || travelCompanion} · `
                : undefined}
            {submitDate}
          </ReviewerDate>
        </div>
        <ReviewRating rating={rating} scale={ratingScale} />
      </ReviewHeader>
      {children}
      <SourceLogo src={logoUrl} />
    </ReviewContainer>
  );
}

type ReviewModalProps = HotelReview & {
  logoUrl: string;
  ratingScale?: GuestReviewRatingScale;
} & HTMLAttributes<HTMLElement>;

export function ReviewCardHorizontal(props: ReviewModalProps) {
  const {
    name,
    submitDate,
    rating,
    text,
    source,
    title,
    tripReason,
    travelCompanion,
    logoUrl,
    ratingScale,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    children,
    ...rest
  } = props;

  return (
    <ReviewContainerHorizontal {...rest}>
      <ReviewHeaderHorizontal>
        <Avatar>{name.charAt(0).toUpperCase()}</Avatar>
        <div
          css={`
            display: flex;
            flex-direction: column;
          `}
        >
          <ReviewerName $forceSmall={true}>{name}</ReviewerName>
          <TripType tripReason={tripReason} travelCompanion={travelCompanion} />
          <ReviewerDate $forceSmall={true}>{submitDate}</ReviewerDate>
        </div>
      </ReviewHeaderHorizontal>
      <div
        css={`
          flex-grow: 1;
        `}
      >
        <div
          css={`
            display: flex;
            flex-direction: row;
            margin-bottom: 8px;
          `}
        >
          {title && (
            <div>
              <Text variant="Body" weight="bold">
                {title}
              </Text>
            </div>
          )}
          <ReviewRating rating={rating} scale={ratingScale} />
        </div>
        <ReviewTextHorizontal>{text}</ReviewTextHorizontal>
        <img
          alt={`logo-${source}`}
          src={logoUrl}
          css={`
            margin-top: 12px;
            height: 16px;
          `}
        />
      </div>
    </ReviewContainerHorizontal>
  );
}

function TripType(props: { tripReason?: string; travelCompanion?: string }) {
  const { tripReason, travelCompanion } = props;
  return tripReason || travelCompanion ? (
    <Text variant="BodySmall" color="textSecondary">
      {tripReason && travelCompanion ? `${tripReason} (${travelCompanion})` : tripReason || travelCompanion}
    </Text>
  ) : null;
}
